import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Award, Star, TrendingUp, Sparkles, CheckCircle2, ArrowRight, Trophy, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useSiteSettings } from "@/contexts/SettingsContext";

const MIN_RATINGS = 10;
const MIN_AVG = 4.5;

export default function SuperstarProgramPage() {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const ssTitle = settings?.superstar_title;
  const ssDesc = settings?.superstar_desc || "Récompense les hôtes qui offrent une expérience exceptionnelle. Plus de visibilité, plus de réservations, un statut premium reconnaissable.";
  const [summary, setSummary] = useState({ avg_rating: 0, rating_count: 0, is_superstar: false });

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/users/${user.id}/ratings`).then(r => setSummary(r.data)).catch(() => {});
  }, [user]);

  const ratingsProgress = Math.min(100, (summary.rating_count / MIN_RATINGS) * 100);
  const avgOk = summary.avg_rating >= MIN_AVG;
  const missingRatings = Math.max(0, MIN_RATINGS - summary.rating_count);

  const benefits = [
    { icon: Trophy, title: "Badge doré sur vos annonces", desc: "Un sceau d'excellence visible par tous les campeurs, partout où votre terrain apparaît." },
    { icon: Eye, title: "Mise en avant gratuite", desc: "Vos terrains apparaissent en tête de la liste d'exploration et dans les sélections." },
    { icon: TrendingUp, title: "Plus de réservations", desc: "Les profils Superstar convertissent jusqu'à 30% mieux : la preuve sociale rassure." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 via-white to-white">
      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100/40 via-white to-emerald-50/30" />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-5 bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-sm px-3 py-1" data-testid="superstar-hero-badge">
            <Award className="h-3.5 w-3.5" /> Programme exclusif
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-zinc-950 mb-4 leading-[1.05]">
            {ssTitle ? ssTitle : (<>Devenez <span className="bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">Hôte Superstar</span></>)}
          </h1>
          <p className="text-base sm:text-lg text-zinc-600 max-w-2xl mx-auto">
            {ssDesc}
          </p>
        </div>
      </section>

      {/* Progression utilisateur */}
      {user && (
        <section className="px-4 pb-8">
          <div className="max-w-3xl mx-auto">
            <Card className={`p-6 rounded-3xl border-2 ${summary.is_superstar ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-lg" : "border-zinc-200 bg-white"}`} data-testid="my-progress-card">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2 className="font-heading text-xl font-bold text-zinc-950">Ma progression</h2>
                  <p className="text-sm text-zinc-500 mt-1">{user.name}</p>
                </div>
                {summary.is_superstar ? (
                  <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-md px-3 py-1.5 text-sm" data-testid="status-superstar">
                    <Award className="h-4 w-4" /> Superstar actif
                  </Badge>
                ) : (
                  <Badge className="bg-zinc-100 text-zinc-600 border-0 px-3 py-1.5 text-sm" data-testid="status-not-yet">En progression</Badge>
                )}
              </div>

              <div className="space-y-5">
                {/* Critere 1 : Nombre d'avis */}
                <div>
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <div className="flex items-center gap-2">
                      {summary.rating_count >= MIN_RATINGS ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <div className="h-4 w-4 rounded-full border-2 border-zinc-300" />}
                      <span className="font-medium text-zinc-900">Au moins {MIN_RATINGS} avis</span>
                    </div>
                    <span className="font-mono font-semibold text-zinc-700" data-testid="progress-count">{summary.rating_count}/{MIN_RATINGS}</span>
                  </div>
                  <Progress value={ratingsProgress} className="h-2" />
                  {missingRatings > 0 && (
                    <p className="text-xs text-zinc-500 mt-1.5">Encore {missingRatings} avis pour valider ce critère</p>
                  )}
                </div>

                {/* Critere 2 : Moyenne */}
                <div>
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <div className="flex items-center gap-2">
                      {avgOk && summary.rating_count > 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <div className="h-4 w-4 rounded-full border-2 border-zinc-300" />}
                      <span className="font-medium text-zinc-900">Moyenne d'au moins {MIN_AVG}/5</span>
                    </div>
                    <span className="font-mono font-semibold text-zinc-700 flex items-center gap-1" data-testid="progress-avg">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      {summary.avg_rating || 0}/5
                    </span>
                  </div>
                  <Progress value={Math.min(100, (summary.avg_rating / 5) * 100)} className="h-2" />
                </div>
              </div>

              {summary.is_superstar && (
                <div className="mt-5 p-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 flex-shrink-0" />
                  <span>Félicitations ! Vous bénéficiez de tous les avantages Superstar. Gardez une moyenne {MIN_AVG}/5 pour conserver votre statut.</span>
                </div>
              )}
            </Card>
          </div>
        </section>
      )}

      {/* Criteres */}
      <section className="px-4 py-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-zinc-950 mb-6 text-center">Les critères</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-6 rounded-2xl border-zinc-200 bg-white" data-testid="criteria-ratings">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <Star className="h-5 w-5 text-amber-600 fill-amber-500" />
              </div>
              <h3 className="font-heading font-bold text-zinc-950 mb-1">{MIN_RATINGS}+ avis reçus</h3>
              <p className="text-sm text-zinc-500">Accumulez au moins {MIN_RATINGS} avis publics de la part de vos campeurs.</p>
            </Card>
            <Card className="p-6 rounded-2xl border-zinc-200 bg-white" data-testid="criteria-avg">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <h3 className="font-heading font-bold text-zinc-950 mb-1">Moyenne {MIN_AVG}/5 minimum</h3>
              <p className="text-sm text-zinc-500">Maintenez une note moyenne supérieure ou égale à {MIN_AVG} étoiles.</p>
            </Card>
          </div>
          <p className="text-center text-xs text-zinc-400 mt-4">Le statut est calculé automatiquement. Si votre moyenne redescend sous {MIN_AVG}/5, le badge est temporairement retiré jusqu'au retour à la qualité attendue.</p>
        </div>
      </section>

      {/* Avantages */}
      <section className="px-4 py-12 bg-gradient-to-b from-white to-zinc-50/60">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-zinc-950 mb-2 text-center">Les avantages</h2>
          <p className="text-center text-zinc-500 text-sm mb-8">Être Superstar, ce n'est pas qu'un badge.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {benefits.map((b, i) => (
              <Card key={i} className="p-5 rounded-2xl border-zinc-200 bg-white hover:shadow-md transition-shadow" data-testid={`benefit-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0">
                    <b.icon className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-zinc-950 mb-1">{b.title}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="px-4 py-14">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-zinc-950 mb-3">Prêt à viser les étoiles ?</h2>
          <p className="text-zinc-500 mb-6">Publiez votre terrain, soignez chaque séjour, et laissez les avis de vos campeurs parler pour vous.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/create-space" data-testid="cta-create-space">
              <Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl px-6 h-11">
                Publier un terrain <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            {user && (
              <Link to="/my-spaces" data-testid="cta-my-spaces">
                <Button variant="outline" className="rounded-xl px-6 h-11">Mes terrains</Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
